import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import SearchInput from "../../components/inputs/SearchInput";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { losBannedAppraisersApi } from "../../api/losBannedAppraisersApi";
import BannedAppraiserForm from "./BannedAppraiserForm";

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v || "");

export default function BannedAppraisersScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const gridRef = useRef(null);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record or null (=add)

  const fetchRows = useCallback(() => {
    setLoading(true);
    losBannedAppraisersApi
      .list()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => {
        setRows([]);
        enqueueSnackbar("Couldn't load banned appraisers", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setEditing(record);
    setFormOpen(true);
  }, []);

  const handleRemove = useCallback(
    (record) => {
      const label =
        [record?.firstName, record?.lastName].filter(Boolean).join(" ") ||
        record?.company ||
        "this appraiser";
      if (!window.confirm(`Remove ${label} from the banned list?`)) return;
      losBannedAppraisersApi
        .remove(record._id)
        .then(() => {
          enqueueSnackbar("Removed from banned list", { variant: "success" });
          setRows((prev) => (prev || []).filter((r) => r._id !== record._id));
        })
        .catch((err) =>
          enqueueSnackbar(
            err?.response?.data?.detail || "Couldn't remove appraiser",
            { variant: "error" },
          ),
        );
    },
    [enqueueSnackbar],
  );

  const handleSaved = useCallback((record) => {
    // Upsert the returned record into the local rows (add OR edit).
    setRows((prev) => {
      const list = prev || [];
      const idx = list.findIndex((r) => r._id === record._id);
      if (idx === -1) return [record, ...list];
      const next = list.slice();
      next[idx] = record;
      return next;
    });
    setFormOpen(false);
  }, []);

  const columnDefs = useMemo(
    () => [
      { headerName: "Last Name", field: "lastName", minWidth: 130, flex: 1 },
      { headerName: "First Name", field: "firstName", minWidth: 120, flex: 1 },
      { headerName: "Company", field: "company", minWidth: 180, flex: 1.4 },
      {
        headerName: "License #",
        field: "appraiserLicenseNumber",
        minWidth: 120,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "State",
        field: "appraiserLicenseState",
        width: 90,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Banned Eff.",
        field: "bannedEffectiveDate",
        width: 120,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "License Exp.",
        colId: "licenseExpirations",
        minWidth: 130,
        valueGetter: (p) => joinList(p.data?.licenseExpirations),
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Appraisals",
        field: "appraisalCount",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => (p.value == null ? "—" : p.value),
      },
      {
        headerName: "EV/LQ Loans",
        colId: "evLqLoansAffected",
        minWidth: 170,
        sortable: false,
        valueGetter: (p) => joinList(p.data?.evLqLoansAffected),
        cellRenderer: (p) => {
          const loans = p.data?.evLqLoansAffected;
          if (!Array.isArray(loans) || !loans.length) return "—";
          return (
            <Flx ac g={0.5} sx={{ flexWrap: "wrap", py: 0.5 }}>
              {loans.map((l) => (
                <Chip
                  key={l}
                  label={l}
                  size="small"
                  sx={{ height: 20, fontSize: 11 }}
                />
              ))}
            </Flx>
          );
        },
      },
      {
        headerName: "Source",
        field: "source",
        minWidth: 180,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "",
        colId: "actions",
        pinned: "right",
        width: 96,
        sortable: false,
        filter: false,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (p) => (
          <Flx ac g={0.25}>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => openEdit(p.data)}>
                <EditOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton size="small" color="error" onClick={() => handleRemove(p.data)}>
                <DeleteOutline sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Flx>
        ),
      },
    ],
    [openEdit, handleRemove],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    }),
    [],
  );

  const getRowId = useCallback((p) => String(p.data?._id), []);

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: "#f8fafc" }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", p: { xs: 2, md: 3 } }}>
        <Flx ac jb g={1.5} sx={{ mb: 2 }} wrap>
          <Flx ac g={1}>
            <GavelOutlined sx={{ color: "#0f172a", fontSize: 22 }} />
            <Txt sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
              Banned Appraisers
              {Array.isArray(rows) ? ` (${rows.length})` : ""}
            </Txt>
          </Flx>
          <Flx ac g={1}>
            <SearchInput
              value={quickFilter}
              onChange={setQuickFilter}
              placeholder="Search name / license…"
            />
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchRows} disabled={loading}>
                  <RefreshRounded />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={openAdd}
            >
              Add Appraiser
            </Button>
          </Flx>
        </Flx>

        <Txt sx={{ fontSize: 12.5, color: "#64748b", mb: 2 }}>
          Global registry of appraisers the desk will not accept. Editing here
          maintains the shared list used to flag valuations during appraisal
          review.
        </Txt>

        <AgGridTableWrapper compactTable>
          <AgGridReact
            ref={gridRef}
            rowData={rows || []}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            quickFilterText={quickFilter}
            domLayout="autoHeight"
            loading={loading}
          />
        </AgGridTableWrapper>
      </Box>

      <BannedAppraiserForm
        open={formOpen}
        record={editing}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  );
}
